import type { FC, ReactNode, SVGProps } from "react"
import { Link } from "@tanstack/react-router"
import { IconChevronRightOutline18 as ChevronRight } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Dot } from "@productlane/ui"

export type SettingsNavRowIcon = FC<SVGProps<SVGSVGElement>>

export const settingsNavGroupClass =
	"sm:-mx-3.5 divide-y divide-gray-300/80 overflow-hidden rounded-[10px] bg-white shadow-card dark:divide-gray-700/80 dark:bg-gray-850 dark:shadow-card-dark"

const rowClass = "flex items-center justify-between gap-4 px-4 py-4"

export function SettingsNavRow({
	icon: Icon,
	title,
	description,
	status,
	link,
	action,
}: {
	icon: SettingsNavRowIcon
	title: string
	description: string
	status?: boolean
	link: string
	// Replaces the status and chevron, and the row stops linking (a button can't sit inside a link).
	action?: ReactNode
}) {
	const body = (
		<div className="flex items-center gap-3">
			<span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-gray-100 text-primary dark:bg-gray-800 [&_svg]:h-[18px] [&_svg]:w-[18px]">
				<Icon />
			</span>
			<div className="flex flex-col gap-0.5">
				<span className="text-sm font-medium text-primary">{title}</span>
				<span className="text-xs font-normal tracking-tight text-secondary">
					{description}
				</span>
			</div>
		</div>
	)

	if (action) {
		return (
			<div className={rowClass}>
				{body}
				<div className="flex flex-none items-center">{action}</div>
			</div>
		)
	}

	return (
		<Link
			to={link}
			className={cn(rowClass, "hover:bg-gray-50 dark:hover:bg-gray-800/50")}
		>
			{body}
			<div className="flex flex-none items-center gap-2">
				{status !== undefined && (
					<span
						className={cn(
							"flex items-center gap-1.5 text-sm",
							status ? "text-secondary" : "text-tertiary opacity-70",
						)}
					>
						<Dot
							color={status ? "green" : "gray"}
							size="sm"
							className={cn(!status && "opacity-50")}
						/>
						{status ? "Enabled" : "Disabled"}
					</span>
				)}
				<ChevronRight className="h-3.5 w-3.5 flex-none text-tertiary" />
			</div>
		</Link>
	)
}
